import React, { useState } from 'react'
import { formatImageUrl, PLACEHOLDER_IMAGE } from '../utils/image-handler'

interface GameImageProps {
  src: string | null | undefined
  alt: string
  className?: string
  fallbackSrc?: string | null
  width?: number | string
  height?: number | string
}

/**
 * GameImage component for rendering game images with proper error handling
 *
 * Features:
 * - Formats URLs correctly (handles BGG partial URLs)
 * - Provides fallback image on error
 * - Supports lazy loading
 * - Shows loading state
 *
 * @param props Component props
 * @returns React component
 */
const GameImage: React.FC<GameImageProps> = ({
  src,
  alt,
  className = '',
  fallbackSrc = null,
  width,
  height,
}) => {
  const [error, setError] = useState(false)
  const [loaded, setLoaded] = useState(false)

  // Format the image URL
  const formattedSrc = error ? PLACEHOLDER_IMAGE : formatImageUrl(src, fallbackSrc)

  return (
    <div className={`game-image-container ${className}`} style={{ position: 'relative' }}>
      {!loaded && (
        <div
          className="game-image-loading"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f3f4f6',
            borderRadius: '4px',
          }}
        >
          <span role="img" aria-label="Loading game image">
            🎲
          </span>
        </div>
      )}
      <img
        src={formattedSrc}
        alt={alt}
        className={`game-image ${loaded ? 'loaded' : 'loading'}`}
        loading="lazy"
        width={width}
        height={height}
        onError={() => setError(true)}
        onLoad={() => setLoaded(true)}
        style={{
          opacity: loaded ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />
    </div>
  )
}

export default GameImage
